import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EntriesDrillDown from '../components/EntriesDrillDown';
import { fmtBRL, fmtPct } from '../lib/format';
import { ArrowDown, Users, Gauge, TrendingDown, ChevronRight, Info } from 'lucide-react';

/**
 * Controladoria › Gestão de Custos
 * Requisitos (Doc Alteração — F1/F2/F3/F4):
 * - Ranking geral de custos com drill-down
 * - Custo por Aluno (global)
 * - Índice de Eficiência Operacional = (Custos + Despesas) ÷ Receita × 100
 *   (quanto MENOR, mais eficiente)
 *
 * Escopo (empresa + período) segue os seletores globais do TopBar.
 */
export default function ControladoriaCustos() {
  const { companyFilter, competence, periodMode, competenceFrom, competenceTo } = useApp();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillItem, setDrillItem] = useState(null);

  const cid = companyFilter === 'all' ? null : companyFilter;

  const params = useMemo(() => {
    const p = { limit: 30 };
    if (cid) p.company_id = cid;
    if (periodMode === 'range') {
      p.competence_from = competenceFrom;
      p.competence_to = competenceTo;
    } else {
      p.competence = competence;
    }
    return p;
  }, [cid, periodMode, competence, competenceFrom, competenceTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await get('/controladoria/custos-summary', params);
        setSummary(r);
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const empty = !loading && summary && summary.total_custos === 0;
  const eficColor = summary && summary.indice_eficiencia < 80 ? '#10B981'
    : summary && summary.indice_eficiencia < 100 ? '#F59E0B'
      : '#EF4444';

  return (
    <div className="fade-in">
      <TopBar
        title="Gestão de Custos"
        subtitle="Controladoria — ranking de custos, custo por aluno e índice de eficiência operacional"
      />

      <div className="px-8 py-7 space-y-5" data-testid="controladoria-custos">
        {/* KPIs de topo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Custo Total"
            value={summary?.total_custos || 0}
            accent="#EF4444"
            icon={ArrowDown}
            testid="cust-total"
            highlight
          />
          <KpiCard
            label="Total de Inscritos"
            value={summary?.total_students || 0}
            format="number"
            accent="#8B5CF6"
            icon={Users}
            testid="cust-inscritos"
          />
          <KpiCard
            label="Custo por Aluno"
            value={summary?.custo_por_aluno || 0}
            accent="#F59E0B"
            icon={TrendingDown}
            testid="cust-por-aluno"
          />
          <KpiCard
            label="Índice de Eficiência"
            value={summary?.indice_eficiencia || 0}
            format="percent"
            accent={eficColor}
            icon={Gauge}
            testid="cust-indice-eficiencia"
          />
        </div>

        {/* Nota educativa sobre o índice */}
        <div className="flex items-start gap-3 px-4 py-3 bg-zinc-900/40 border border-zinc-800 rounded-md">
          <Info size={14} className="text-zinc-500 shrink-0 mt-0.5" />
          <div className="text-[11.5px] text-zinc-400 leading-relaxed">
            <strong className="text-zinc-300">Índice de Eficiência Operacional</strong> = (Custos + Despesas) ÷ Receita Total × 100.
            {' '}Quanto <strong className="text-emerald-400">menor</strong>, mais eficiente é a operação para gerar cada unidade de receita.
            Valores {'<'} 80% são bem saudáveis; entre 80–100% exige atenção; {'>'}100% indica prejuízo operacional.
          </div>
        </div>

        {/* Ranking de custos */}
        <SectionCard
          title="Ranking de Custos"
          subtitle="Ordenado por valor · Clique numa linha para ver o discriminado"
          testid="cust-ranking-card"
        >
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Carregando…</div>
          ) : empty ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              Nenhum custo encontrado neste período.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table" data-testid="cust-ranking-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Categoria / Centro de Custo</th>
                    <th className="text-center">Qtd. Lançamentos</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">% do Total</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.ranking || []).map((r, i) => (
                    <tr
                      key={`${r.category_id || r.cost_center_id || r.name}-${i}`}
                      className="cursor-pointer hover:bg-zinc-900/40"
                      onClick={() => setDrillItem(r)}
                      data-testid={`cust-row-${i}`}
                    >
                      <td className="text-zinc-500 font-mono text-xs">{i + 1}</td>
                      <td className="text-zinc-100 font-medium">{r.name}</td>
                      <td className="text-center font-mono text-zinc-400">{r.count}</td>
                      <td className="text-right font-mono red-text">{fmtBRL(r.total)}</td>
                      <td className="text-right font-mono text-zinc-300">{fmtPct(r.pct)}</td>
                      <td className="text-zinc-600">
                        <ChevronRight size={14} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Drill-down */}
      <EntriesDrillDown
        open={!!drillItem}
        onClose={() => setDrillItem(null)}
        type="CUSTO"
        competence={periodMode === 'range' ? competenceTo : competence}
        companyId={cid}
        title={drillItem ? `Custos — ${drillItem.name}` : ''}
        filterCategoryId={drillItem?.category_id}
        filterCostCenterId={drillItem?.cost_center_id}
        filterName={drillItem?.name}
      />
    </div>
  );
}
